import React, { useState, useMemo, useEffect } from "react";
import {
  Wrapper, Sidebar, SidebarTitle, MenuButton,
  MainContent, CardsContainer, Card, Title, Value, ChartContainer, ChartRow,
  FilterContainer, FilterSelect, DateRangeContainer, DateInput, ApplyButton,
  KpiCard, KpiContainer, KpiValue, KpiTitle, KpiTrend, KpiDescription,
  InfoIcon, TooltipContent
} from './styles';

import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell, LineChart, Line, PieChart, Pie, Legend, 
  AreaChart, Area, ComposedChart, ReferenceLine
} from "recharts";
import { 
  FaArrowDown, FaArrowUp, FaEquals, FaChartLine, FaChartBar, 
  FaFileInvoice, FaInfoCircle, FaCalendarAlt, FaSearch, FaMinus
} from "react-icons/fa";


// Componente para tooltip de ajuda
const InfoTooltip = ({ content }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div style={{ position: 'relative', display: 'inline-block', marginLeft: '5px' }}>
      <InfoIcon 
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <FaInfoCircle size={14} />
      </InfoIcon>
      {showTooltip && <TooltipContent>{content}</TooltipContent>}
    </div>
  );
};

// Componente para filtros
const DashboardFilters = ({ filters, setFilters, onApplyFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  const handleChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const applyFilters = () => {
    setFilters(localFilters);
    onApplyFilters(localFilters);
  };
  
  return (
    <FilterContainer>
      <FilterSelect 
        value={localFilters.periodo} 
        onChange={(e) => handleChange('periodo', e.target.value)}
      >
        <option value="ultimos30">Últimos 30 dias</option>
        <option value="ultimos90">Últimos 90 dias</option>
        <option value="esseAno">Esse ano</option>
        <option value="personalizado">Período personalizado</option>
      </FilterSelect>
      
      {localFilters.periodo === 'personalizado' && (
        <DateRangeContainer>
          <FaCalendarAlt />
          <DateInput 
            type="date" 
            value={localFilters.dataInicio} 
            onChange={(e) => handleChange('dataInicio', e.target.value)} 
          />
          <span>até</span>
          <DateInput 
            type="date" 
            value={localFilters.dataFim} 
            onChange={(e) => handleChange('dataFim', e.target.value)} 
          />
        </DateRangeContainer>
      )}
      
      <FilterSelect 
        value={localFilters.centroCusto} 
        onChange={(e) => handleChange('centroCusto', e.target.value)}
      >
        <option value="todos">Todos centros de custo</option>
        <option value="administrativo">Administrativo</option>
        <option value="operacional">Operacional</option>
        <option value="comercial">Comercial</option>
        <option value="marketing">Marketing</option>
      </FilterSelect>
      
      <ApplyButton onClick={applyFilters}>
        <FaSearch style={{ marginRight: '8px' }} />
        Aplicar Filtros
      </ApplyButton>
    </FilterContainer>
  );
};

// Extrai os menus em um componente separado para reutilização
const SidebarMenu = ({ navigate, activeRoute }) => {
  const menuItems = [
    { label: "Voltar", path: "/Home", icon: null },
    { label: "Dashboard", path: "/financeiro/dashboard", icon: FaChartLine },
    { label: "Contas a Pagar", path: "/financeiro/contas-pagar", icon: FaFileInvoice },
    { label: "Contas a Receber", path: "/financeiro/contas-receber", icon: FaFileInvoice },
    { label: "Fornecedores", path: "/financeiro/fornecedores", icon: null },
    { label: "Relatórios", path: "/financeiro/relatorios", icon: FaChartBar },
    { label: "Configurações", path: "/financeiro/configuracoes", icon: null }
  ];

  return (
    <>
      {menuItems.map((item, index) => (
        <MenuButton 
          key={index} 
          onClick={() => navigate(item.path)}
          active={activeRoute === item.path}
        >
          {item.icon && <item.icon style={{ marginRight: '8px' }} />}
          {item.label}
        </MenuButton>
      ))}
    </>
  );
};

// Componente reutilizável para estatísticas
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
  // Formata o valor como moeda brasileira
  const formattedValue = new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL' 
  }).format(value);

  return (
    <Card borderColor={color}>
      <Icon size={24} color={color} />
      <Value cor={color}>{formattedValue}</Value>
      <Title>{title}</Title>
      {subtitle && <p style={{ fontSize: '12px', color: '#666' }}>{subtitle}</p>}
    </Card>
  );
};

// Componente para KPIs
const KPI = ({ title, value, trend, description, info }) => {
  const isNegative = trend < 0;
  const isNeutral = trend === 0;
  
  const renderTrendIcon = () => {
    if (isNeutral) return <FaMinus color="#888" />;
    if (isNegative) return <FaArrowDown color="#dc2626" />;
    return <FaArrowUp color="#16a34a" />;
  };
  
  
  const trendColor = isNeutral ? '#888' : (isNegative ? '#dc2626' : '#16a34a');
  const trendText = `${isNegative ? '' : '+'}${trend}%`;
  
  return (
    <KpiCard>
      <KpiTitle>
        {title}
        {info && <InfoTooltip content={info} />}
      </KpiTitle>
      <KpiValue>{value}</KpiValue>
      <KpiTrend color={trendColor}>
        {renderTrendIcon()}
        <span>{trendText}</span>
      </KpiTrend>
      <KpiDescription>{description}</KpiDescription>
    </KpiCard>
  );
};

// Componente para renderizar gráficos
const Chart = ({ title, type, data, config, info }) => {
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={config.xAxis} axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              formatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            {config.legend && <Legend />}
            {config.bars.map((bar, index) => (
              <Bar 
                key={index} 
                dataKey={bar.dataKey} 
                fill={bar.fill} 
                name={bar.name}
                radius={[4, 4, 0, 0]}
              >
                {config.customCells && data.map((_, index) => (
                  <Cell key={index} fill={config.colors[index]} />
                ))}
              </Bar>
            ))}
            {config.referenceLine && (
              <ReferenceLine 
                y={config.referenceLine.value} 
                stroke={config.referenceLine.color} 
                strokeDasharray="3 3" 
                label={config.referenceLine.label} 
              />
            )}
          </BarChart>
        );
      case 'line':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={config.xAxis} axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              formatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            {config.legend && <Legend />}
            {config.lines.map((line, index) => (
              <Line 
                key={index}
                type="monotone" 
                dataKey={line.dataKey} 
                stroke={line.stroke} 
                name={line.name} 
                strokeWidth={3} 
                dot={{ r: 4, fill: line.stroke, strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        );
      case 'area':
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={config.xAxis} axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              formatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            {config.legend && <Legend />}
            {config.areas.map((area, index) => (
              <Area
                key={index}
                type="monotone"
                dataKey={area.dataKey}
                stroke={area.stroke}
                fill={area.fill}
                name={area.name}
                fillOpacity={0.3}
              />
            ))}
          </AreaChart>
        );
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={config.dataKey}
              nameKey={config.nameKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((entry, index) => (
                <Cell key={index} fill={config.colors[index % config.colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            <Legend />
          </PieChart>
        );
      case 'composed':
        return (
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={config.xAxis} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" axisLine={false} tickLine={false} />
            <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} />
            <Tooltip 
              formatter={(value, name) => [
                `R$ ${value.toLocaleString('pt-BR')}`, 
                name
              ]}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            <Legend />
            {config.bars && config.bars.map((bar, index) => (
              <Bar 
                key={`bar-${index}`}
                yAxisId={bar.yAxisId || "left"}
                dataKey={bar.dataKey} 
                fill={bar.fill} 
                name={bar.name}
                radius={[4, 4, 0, 0]}
              />
            ))}
            {config.lines && config.lines.map((line, index) => (
              <Line 
                key={`line-${index}`}
                yAxisId={line.yAxisId || "right"}
                type="monotone" 
                dataKey={line.dataKey} 
                stroke={line.stroke} 
                name={line.name} 
                strokeWidth={3} 
                dot={{ r: 4, fill: line.stroke, strokeWidth: 2 }}
              />
            ))}
          </ComposedChart>
        );
      default:
        return null;
    }
  };

  return (
    <ChartContainer>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
        <h2>{title}</h2>
        {info && <InfoTooltip content={info} />}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default function Dashboard() {
  // Estado dos filtros
  const [filters, setFilters] = useState({
    periodo: 'ultimos30',
    dataInicio: '2025-03-01',
    dataFim: '2025-03-31',
    centroCusto: 'todos'
  });
  
  // Resumo financeiro
  const [resumo, setResumo] = useState({
    total_receber: 25000,
    total_pagar: 17000,
    saldo: 8000,
    total_vencido: 3200,
    total_recebido: 31500,
    total_pago: 24300
  });

  const navigate = useNavigate();
  
  // Simula a busca de dados quando os filtros são alterados
  const fetchData = (activeFilters) => {
    console.log("Buscando dados com filtros:", activeFilters);
    // Aqui seria uma chamada API real para buscar dados
    // Por enquanto, vamos simular uma atualização dos dados com pequenas variações
    
    // Simulação de variação de dados baseada no centro de custo
    let multiplicador = 1;
    if (activeFilters.centroCusto === 'administrativo') multiplicador = 0.8;
    if (activeFilters.centroCusto === 'operacional') multiplicador = 1.2;
    if (activeFilters.centroCusto === 'comercial') multiplicador = 1.5;
    if (activeFilters.centroCusto === 'marketing') multiplicador = 0.9;
    
    setResumo({
      total_receber: 25000 * multiplicador,
      total_pagar: 17000 * multiplicador,
      saldo: (25000 - 17000) * multiplicador,
      total_vencido: 3200 * multiplicador,
      total_recebido: 31500 * multiplicador,
      total_pago: 24300 * multiplicador
    });
  };

  // Efeito para buscar dados quando componente monta
  useEffect(() => {
    fetchData(filters);
  }, []);
  
  // Handler para aplicar filtros
  const handleApplyFilters = (newFilters) => {
    fetchData(newFilters);
  };

  // Preparação dos dados utilizando useMemo para evitar recálculos desnecessários
  const dadosComparativo = useMemo(() => [
    { nome: "A Receber", valor: resumo.total_receber },
    { nome: "A Pagar", valor: resumo.total_pagar },
    { nome: "Saldo", valor: resumo.saldo },
  ], [resumo]);

  const dadosFluxoMensal = useMemo(() => [
    { mes: "Jan", receitas: 10000, despesas: 7000, saldo: 3000 },
    { mes: "Fev", receitas: 12000, despesas: 8000, saldo: 4000 },
    { mes: "Mar", receitas: 9000, despesas: 6000, saldo: 3000 },
    { mes: "Abr", receitas: 11500, despesas: 7500, saldo: 4000 },
    { mes: "Mai", receitas: 13000, despesas: 8200, saldo: 4800 },
    { mes: "Jun", receitas: 10800, despesas: 7800, saldo: 3000 }
  ], []);

  const dadosCategorias = useMemo(() => [
    { categoria: "Aluguel", total: 5000 },
    { categoria: "Marketing", total: 3000 },
    { categoria: "RH", total: 2500 },
    { categoria: "Impostos", total: 4200 },
    { categoria: "Insumos", total: 2300 }
  ], []);

  const dadosEvolucao = useMemo(() => [
    { data: "Jan", saldo: 2000, meta: 2500 },
    { data: "Fev", saldo: 5000, meta: 3000 },
    { data: "Mar", saldo: 3500, meta: 3500 },
    { data: "Abr", saldo: 8000, meta: 4000 },
    { data: "Mai", saldo: 6500, meta: 4500 },
    { data: "Jun", saldo: 7200, meta: 5000 }
  ], []);
  
  const dadosInadimplencia = useMemo(() => [
    { mes: "Jan", taxa: 4.2, media_mercado: 5.1 },
    { mes: "Fev", taxa: 3.8, media_mercado: 5.0 },
    { mes: "Mar", taxa: 3.5, media_mercado: 4.9 },
    { mes: "Abr", taxa: 3.9, media_mercado: 5.1 },
    { mes: "Mai", taxa: 3.2, media_mercado: 4.8 },
    { mes: "Jun", taxa: 2.9, media_mercado: 4.7 }
  ], []);
  
  const dadosProjecao = useMemo(() => [
    { mes: "Jul", previsto: 12500, realizado: 0 },
    { mes: "Ago", previsto: 13200, realizado: 0 },
    { mes: "Set", previsto: 14000, realizado: 0 },
    { mes: "Out", previsto: 13500, realizado: 0 },
    { mes: "Nov", previsto: 15000, realizado: 0 },
    { mes: "Dez", previsto: 18000, realizado: 0 }
  ], []);

  // Configurações dos gráficos
  const chartConfigs = {
    comparativo: {
      type: 'bar',
      data: dadosComparativo,
      config: {
        xAxis: 'nome',
        legend: false,
        customCells: true,
        colors: ["#16a34a", "#dc2626", "#2563eb"],
        bars: [{ dataKey: 'valor' }]
      },
      info: "Comparativo entre valores a receber, a pagar e o saldo resultante no período selecionado."
    },
    fluxoMensal: {
      type: 'composed',
      data: dadosFluxoMensal,
      config: {
        xAxis: 'mes',
        legend: true,
        bars: [
          { dataKey: 'receitas', fill: '#16a34a', name: 'Receitas', yAxisId: "left" },
          { dataKey: 'despesas', fill: '#dc2626', name: 'Despesas', yAxisId: "left" }
        ],
        lines: [
          { dataKey: 'saldo', stroke: '#2563eb', name: 'Saldo', yAxisId: "right" }
        ]
      },
      info: "Análise de receitas e despesas mensais com o saldo resultante em cada período."
    },
    categorias: {
      type: 'pie',
      data: dadosCategorias,
      config: {
        dataKey: 'total',
        nameKey: 'categoria',
        colors: ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe']
      },
      info: "Distribuição de despesas por categoria, mostrando as áreas que consomem maior parte do orçamento."
    },
    evolucao: {
      type: 'area',
      data: dadosEvolucao,
      config: {
        xAxis: 'data',
        legend: true,
        areas: [
          { dataKey: 'saldo', stroke: '#2563eb', fill: '#2563eb', name: 'Saldo' }
        ]
      },
      info: "Evolução do saldo financeiro ao longo do tempo, mostrando a tendência de crescimento ou redução."
    },
    inadimplencia: {
      type: 'line',
      data: dadosInadimplencia,
      config: {
        xAxis: 'mes',
        legend: true,
        lines: [
          { dataKey: 'taxa', stroke: '#dc2626', name: 'Taxa de Inadimplência' },
          { dataKey: 'media_mercado', stroke: '#888888', name: 'Média do Mercado' }
        ]
      },
      info: "Comparativo entre a taxa de inadimplência da empresa e a média do mercado."
    },
    projecao: {
      type: 'bar',
      data: dadosProjecao,
      config: {
        xAxis: 'mes',
        legend: true,
        bars: [
          { dataKey: 'previsto', fill: '#9333ea', name: 'Receita Prevista' },
          { dataKey: 'realizado', fill: '#16a34a', name: 'Receita Realizada' }
        ],
      },
      info: "Projeção financeira para os próximos meses baseada em histórico, contratos e tendências."
    }
  };

  // Cards de estatísticas
  const statCards = [
    {
      title: "Total a Receber",
      value: resumo.total_receber,
      icon: FaArrowDown,
      color: "#16a34a",
      subtitle: "Vencimento nos próximos 30 dias"
    },
    {
      title: "Total a Pagar",
      value: resumo.total_pagar,
      icon: FaArrowUp,
      color: "#dc2626",
      subtitle: "Vencimento nos próximos 30 dias"
    },
    {
      title: "Saldo Projetado",
      value: resumo.saldo,
      icon: FaEquals,
      color: "#2563eb",
      subtitle: "Após todos os pagamentos"
    }
  ];
  
  // KPIs financeiros
  const kpis = [
    {
      title: "Liquidez Corrente",
      value: "1.47",
      trend: 5.2,
      description: "Em relação ao mês anterior",
      info: "Relação entre ativos e passivos de curto prazo. Valores acima de 1 indicam boa saúde financeira."
    },
    {
      title: "Ciclo de Conversão",
      value: "18 dias",
      trend: -12.5,
      description: "Em relação ao mês anterior",
      info: "Tempo médio entre pagar fornecedores e receber de clientes. Quanto menor, melhor."
    },
    {
      title: "Margem Operacional",
      value: "24.8%",
      trend: 1.3,
      description: "Em relação ao mês anterior",
      info: "Percentual de lucro em relação às vendas após custos operacionais."
    },
    {
      title: "Inadimplência",
      value: "2.9%",
      trend: -9.4,
      description: "Em relação ao mês anterior",
      info: "Percentual de contas a receber vencidas há mais de 30 dias."
    }
  ];

  return (
    <Wrapper>
      <Sidebar>
        <SidebarTitle>
          <FaChartLine style={{ marginRight: '8px' }} />
          Financeiro
        </SidebarTitle>
        <SidebarMenu navigate={navigate} activeRoute="/financeiro/dashboard" />
      </Sidebar>

      <MainContent>
        <Header />
        <h1 style={{ marginBottom: "20px", fontWeight: 600 }}>Dashboard Financeiro</h1>
        
        <DashboardFilters 
          filters={filters} 
          setFilters={setFilters}
          onApplyFilters={handleApplyFilters}
        />

        <CardsContainer>
          {statCards.map((card, index) => (
            <StatCard 
              key={index}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              subtitle={card.subtitle}
            />
          ))}
        </CardsContainer>
        
        <KpiContainer>
          {kpis.map((kpi, index) => (
            <KPI 
              key={index}
              title={kpi.title}
              value={kpi.value}
              trend={kpi.trend}
              description={kpi.description}
              info={kpi.info}
            />
          ))}
        </KpiContainer>

        <ChartRow>
          <Chart 
            title="Comparativo Financeiro" 
            {...chartConfigs.comparativo} 
          />
          <Chart 
            title="Fluxo Mensal" 
            {...chartConfigs.fluxoMensal} 
          />
        </ChartRow>

        <ChartRow>
          <Chart 
            title="Categorias de Despesa" 
            {...chartConfigs.categorias} 
          />
          <Chart 
            title="Evolução do Saldo" 
            {...chartConfigs.evolucao} 
          />
        </ChartRow>
        
        <ChartRow>
          <Chart 
            title="Taxa de Inadimplência" 
            {...chartConfigs.inadimplencia} 
          />
          <Chart 
            title="Projeção de Receitas" 
            {...chartConfigs.projecao} 
          />
        </ChartRow>
      </MainContent>

      <Footer />
    </Wrapper>
  );
}